import React, { useEffect, useState } from "react";
import ErrorRow from "Common/ErrorRow";
import BreadCrumb from "Common/BreadCrumb";
import { AlertTriangle, Search, RefreshCw } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import { useFocusedRow } from "hooks/useFocusedRecord";
import {
  FocusNotFound,
  focusRowClass,
  scrollRowIntoView,
} from "Common/FocusedRow";
import useComplaints, {
  useAddComplaintComment,
  useComplaintSummary,
  useUpdateComplaint,
} from "hooks/useComplaints";
import {
  COMPLAINT_STATUSES,
  ComplaintStatus,
  STATUS_LABELS,
  UserComplaint,
} from "services/complaint.service";

const STATUS_COLORS: Record<ComplaintStatus, string> = {
  open: "bg-red-50 text-red-700",
  investigating: "bg-amber-50 text-amber-700",
  resolved: "bg-green-50 text-green-700",
  dismissed: "bg-gray-100 text-gray-600",
};

const SEVERITY_COLORS: Record<string, string> = {
  low: "bg-gray-100 text-gray-700",
  medium: "bg-yellow-50 text-yellow-800",
  high: "bg-orange-50 text-orange-800",
  critical: "bg-red-100 text-red-800",
};

const Complaints = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  // Per-complaint working state, keyed by id so expanding a second row does
  // not inherit what was half-typed into the first.
  const [drafts, setDrafts] = useState<
    Record<string, { status: ComplaintStatus; resolution: string; comment: string }>
  >({});

  const { data: response, isLoading, isError, refetch } = useComplaints({
    page,
    limit: 20,
    status: statusFilter || undefined,
  });
  const { data: summaryResponse } = useComplaintSummary();
  const updateComplaint = useUpdateComplaint();
  const addComment = useAddComplaintComment();

  const complaints: UserComplaint[] = response?.data?.data ?? [];
  const total = response?.data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);
  const summary = summaryResponse?.data;

  /**
   * Opens the complaint an operator followed here from an ops alert.
   *
   * The complaint's own detail lives in an expanded row rather than a route,
   * so following the alert means expanding it — highlighting alone would still
   * leave the operator a click away from what they were paged about.
   */
  const { notFound: focusNotFound, dismissFocus, isFocused, clearHighlight } =
    useFocusedRow(complaints, isLoading, setExpanded);

  const draftFor = (complaint: UserComplaint) =>
    drafts[complaint._id] ?? {
      status: complaint.status,
      resolution: complaint.resolution ?? "",
      comment: "",
    };

  const setDraft = (
    id: string,
    patch: Partial<{ status: ComplaintStatus; resolution: string; comment: string }>
  ) =>
    setDrafts((previous) => ({
      ...previous,
      [id]: { ...(previous[id] ?? { status: "open", resolution: "", comment: "" }), ...patch },
    }));

  const save = (complaint: UserComplaint) => {
    const draft = draftFor(complaint);

    // The API requires a resolution to resolve, because the complainant is
    // sent that text — catch it here rather than bouncing off a 400.
    if (draft.status === "resolved" && !draft.resolution.trim()) {
      toast.error("Add a resolution before resolving — the complainant is sent it");
      return;
    }

    updateComplaint.mutate(
      {
        id: complaint._id,
        status: draft.status,
        resolution: draft.resolution.trim() || undefined,
      },
      {
        onSuccess() {
          toast.success(
            draft.status === "resolved" || draft.status === "dismissed"
              ? "Complaint closed — the complainant has been notified"
              : "Complaint updated"
          );
        },
        onError(error: any) {
          toast.error(error?.response?.data?.message || "Update failed");
        },
      }
    );
  };

  const postComment = (complaint: UserComplaint) => {
    const body = draftFor(complaint).comment.trim();
    if (!body) return;

    addComment.mutate(
      { id: complaint._id, body },
      {
        onSuccess() {
          setDraft(complaint._id, { comment: "" });
          toast.success("Note added");
        },
        onError(error: any) {
          toast.error(error?.response?.data?.message || "Could not add the note");
        },
      }
    );
  };

  const filtered = search
    ? complaints.filter(
        (c) =>
          c.description?.toLowerCase().includes(search.toLowerCase()) ||
          c.complaintBy?.firstName?.toLowerCase().includes(search.toLowerCase()) ||
          c.complaintBy?.lastName?.toLowerCase().includes(search.toLowerCase()) ||
          c.complaintBy?.email?.toLowerCase().includes(search.toLowerCase()) ||
          c.user?.firstName?.toLowerCase().includes(search.toLowerCase()) ||
          c.user?.lastName?.toLowerCase().includes(search.toLowerCase())
      )
    : complaints;

  return (
    <React.Fragment>
      <BreadCrumb title="Complaints" pageTitle="Support" />
      <ToastContainer closeButton={false} limit={1} />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          {[
            { label: "Open", value: summary.open },
            { label: "Investigating", value: summary.investigating },
            { label: "Unassigned", value: summary.unassigned },
            { label: "Critical", value: summary.critical },
            { label: "Resolved", value: summary.resolved },
          ].map((card) => (
            <div
              key={card.label}
              className="bg-white rounded-lg border border-gray-200 px-4 py-3"
            >
              <p className="text-xs text-gray-500">{card.label}</p>
              <p className="text-xl font-semibold text-gray-900">{card.value}</p>
            </div>
          ))}
        </div>
      )}

      {focusNotFound && <FocusNotFound onDismiss={dismissFocus} />}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-2.5 size-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter this page by name, email or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-gray-400"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-600"
          >
            <option value="">All statuses</option>
            {COMPLAINT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {STATUS_LABELS[status]}
              </option>
            ))}
          </select>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="size-4" /> Refresh
          </button>
          <span className="sm:ml-auto text-sm text-gray-500">{total} total</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table data-stack className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Filed By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Against</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <ErrorRow colSpan={5} label="Couldn't load complaints" onRetry={refetch} />
              ) : isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-16 text-center">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <AlertTriangle className="size-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No complaints found</p>
                  </td>
                </tr>
              ) : (
                filtered.map((complaint) => {
                  const draft = draftFor(complaint);

                  return (
                    <React.Fragment key={complaint._id}>
                      <tr
                        ref={scrollRowIntoView(isFocused(complaint._id))}
                        className={`hover:bg-gray-50 transition-colors cursor-pointer ${focusRowClass(isFocused(complaint._id))}`}
                        onClick={() => {
                          clearHighlight();
                          setExpanded(
                            expanded === complaint._id ? null : complaint._id
                          );
                        }}
                      >
                        <td data-label="Filed By" data-cell="title" className="px-4 py-3">
                          <p className="font-medium text-gray-900">
                            {complaint.complaintBy?.firstName}{" "}
                            {complaint.complaintBy?.lastName}
                          </p>
                          <p className="text-xs text-gray-400">
                            {complaint.complaintBy?.email}
                          </p>
                        </td>
                        <td data-label="Against" data-cell="block" className="px-4 py-3">
                          <p className="font-medium text-gray-900">
                            {complaint.user?.firstName} {complaint.user?.lastName}
                          </p>
                          <p className="text-xs text-gray-400">
                            {complaint.user?.email}
                          </p>
                        </td>
                        <td data-label="Description" data-cell="block" className="px-4 py-3 text-gray-600 sm:max-w-xs">
                          <p className="truncate">{complaint.description}</p>
                        </td>
                        <td data-label="Status" className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                              STATUS_COLORS[complaint.status] ?? STATUS_COLORS.open
                            }`}
                          >
                            {STATUS_LABELS[complaint.status] ?? complaint.status}
                          </span>
                          {complaint.severity ? (
                            <span
                              className={`ml-1.5 inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                SEVERITY_COLORS[complaint.severity] ?? ""
                              }`}
                            >
                              {complaint.severity}
                            </span>
                          ) : null}
                        </td>
                        <td data-label="Date" className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {new Date(complaint.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                      {expanded === complaint._id && (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-6 py-4 bg-gray-50 border-b border-gray-200"
                          >
                            <p className="text-sm font-medium text-gray-700 mb-1">
                              Full Description
                            </p>
                            <p className="text-sm text-gray-600 whitespace-pre-wrap mb-4">
                              {complaint.description}
                            </p>

                            {complaint.comments && complaint.comments.length > 0 && (
                              <div className="mb-4">
                                <p className="text-sm font-medium text-gray-700 mb-1">
                                  History
                                </p>
                                <ul className="space-y-1">
                                  {complaint.comments.map((entry, index) => (
                                    <li
                                      key={`${entry.createdAt}-${index}`}
                                      className="text-xs text-gray-600"
                                    >
                                      <span className="text-gray-400">
                                        {new Date(entry.createdAt).toLocaleString()}
                                        {entry.authorName ? ` · ${entry.authorName}` : ""}
                                        {" — "}
                                      </span>
                                      {entry.body}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-3 mb-3">
                              <select
                                value={draft.status}
                                onChange={(e) =>
                                  setDraft(complaint._id, {
                                    status: e.target.value as ComplaintStatus,
                                  })
                                }
                                className="px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-700"
                              >
                                {COMPLAINT_STATUSES.map((status) => (
                                  <option key={status} value={status}>
                                    {STATUS_LABELS[status]}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="text"
                                value={draft.resolution}
                                onChange={(e) =>
                                  setDraft(complaint._id, { resolution: e.target.value })
                                }
                                placeholder="Resolution — the complainant is sent this"
                                className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-200 placeholder:text-gray-400"
                              />
                              <button
                                onClick={() => save(complaint)}
                                disabled={updateComplaint.isLoading}
                                className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                              >
                                {updateComplaint.isLoading ? "Saving..." : "Save"}
                              </button>
                            </div>

                            <div className="flex gap-3">
                              <input
                                type="text"
                                value={draft.comment}
                                onChange={(e) =>
                                  setDraft(complaint._id, { comment: e.target.value })
                                }
                                placeholder="Add an internal note (not sent to anyone)"
                                className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-200 placeholder:text-gray-400"
                              />
                              <button
                                onClick={() => postComment(complaint)}
                                disabled={addComment.isLoading}
                                className="px-4 py-2 text-sm rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              >
                                Add note
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default Complaints;
